import React from 'react';
import { computeAll } from '../lib/calc';
import { CFG } from '../lib/config';
import { krw, pct } from '../lib/format';
import {
  WaterfallChart,
  BarChart,
  LineProjectionChart,
  CashflowStackChart,
} from './charts';
import './ReportViewer.css';

export default function ReportViewer({ customer, reportId, createdAt }) {
  const results = React.useMemo(() => computeAll(customer), [customer]);
  const adv = results.find(r => r.scenario_key === 'tax_advantaged');
  const tax = results.find(r => r.scenario_key === 'taxable');

  const generatedAt = createdAt
    ? new Date(createdAt).toLocaleString('ko-KR')
    : new Date().toLocaleString('ko-KR');

  const pvTotal =
    customer.current_personal_pension + customer.current_retirement_pension;

  return (
    <div className="rv-container">

      {/* ── Page 1: 현황 요약 ─────────────────────────── */}
      <div className="rv-header">
        <div className="rv-title">은퇴 현금흐름 설계 리포트</div>
        <div className="rv-subtitle">
          고객: <b>{customer.customer_name}</b> · 생성: {generatedAt}<br />
          현재 {customer.current_age}세 → 목표 수령 시작 {customer.retirement_start_age}세
          ({adv.years_to_retire}년 남음)
        </div>
        <div className="rv-pill">
          <span className="rv-badge">핵심 가정</span>
          <span>은퇴 후 <b>원금 유지</b>(운용수익으로만 인출)</span>
        </div>
      </div>

      <div className="rv-grid rv-grid-3">
        <div className="rv-card rv-kpi">
          <div className="rv-kpi-v">{krw(customer.target_monthly_pension)}</div>
          <div className="rv-kpi-l">희망 월 수익</div>
        </div>
        <div className="rv-card rv-kpi">
          <div className="rv-kpi-v">{krw(customer.expected_nps_monthly)}</div>
          <div className="rv-kpi-l">국민연금(예상)</div>
        </div>
        <div className="rv-card rv-kpi">
          <div className="rv-kpi-v">{krw(adv.needed_monthly_cashflow)}</div>
          <div className="rv-kpi-l">필요 현금흐름(갭)</div>
        </div>
      </div>

      <div className="rv-section">
        <div className="rv-card">
          <h2>1) 희망 월 수익 vs 국민연금(갭)</h2>
          <WaterfallChart
            target={customer.target_monthly_pension}
            nps={customer.expected_nps_monthly}
            gap={adv.needed_monthly_cashflow}
          />
          <p className="rv-note">
            필요 현금흐름 = max(0, 희망 월 수익 - 국민연금).{' '}
            {CFG.TARGET_IS_AFTER_TAX
              ? '목표 금액은 세후(실수령)로 해석하고, 세금을 고려해 필요 자산을 역산합니다.'
              : '목표 금액은 세전으로 해석합니다.'}
          </p>
        </div>
      </div>
      <div className="rv-page-break" />

      {/* ── Page 2: 필요 은퇴자산 ─────────────────────── */}
      <div className="rv-section-title">2) 목표를 "필요 은퇴자산"으로 변환</div>

      <div className="rv-grid rv-grid-2">
        <div className="rv-card">
          <h2>필요 은퇴자산(절세계좌 가정)</h2>
          <div className="rv-kpi">
            <div className="rv-kpi-v">{krw(adv.required_retirement_asset)}</div>
            <div className="rv-kpi-l">
              은퇴 후 목표 운용수익률 {pct(customer.withdrawal_target_yield)} (연)
              · 운용비용 {pct(CFG.ANNUAL_MANAGEMENT_FEE)} 반영
            </div>
          </div>
          <p className="rv-note">
            원금 유지 가정: 필요자산 = (연 인출 필요액) ÷ (은퇴 운용수익률 - 연 운용비용).
            세금은 시나리오별로 추가 반영합니다.
          </p>
        </div>

        <div className="rv-card">
          <h2>현재 자산(연금계좌)</h2>
          <table className="rv-table">
            <thead>
              <tr><th>항목</th><th>금액</th></tr>
            </thead>
            <tbody>
              <tr><td>개인연금</td><td>{krw(customer.current_personal_pension)}</td></tr>
              <tr><td>퇴직연금</td><td>{krw(customer.current_retirement_pension)}</td></tr>
              <tr><td><b>합계</b></td><td><b>{krw(pvTotal)}</b></td></tr>
            </tbody>
          </table>
          <p className="rv-note">
            위 금액이 목표 수령 시작 시점까지{' '}
            {pct(customer.accumulation_target_return)}(연) 목표수익률로 적립된다고 가정합니다
            (수수료/세금 반영은 시나리오별).
          </p>
        </div>
      </div>

      <div className="rv-section">
        <div className="rv-card">
          <h2>시나리오별 필요한 은퇴자산 비교(추정)</h2>
          <BarChart
            labels={[adv.scenario_name, tax.scenario_name]}
            values={[adv.required_retirement_asset, tax.required_retirement_asset]}
            title="원금 유지 가정 시 필요한 은퇴자산(추정)"
            yTitle="억원"
            unit="eokwon"
          />
          <p className="rv-note">
            일반계좌는 이자/배당 원천징수(기본 15.4%)를 단순 적용합니다.
            절세계좌는 연금소득세(연령별 3.3~5.5%)를 단순 적용합니다.
          </p>
        </div>
      </div>
      <div className="rv-page-break" />

      {/* ── Page 3: 월 납입액 비교 ───────────────────── */}
      <div className="rv-section-title">3) 절세계좌 vs 일반계좌: 월 납입액 비교</div>

      <div className="rv-grid rv-grid-2">
        <div className="rv-card">
          <h2>절세계좌 월 납입액</h2>
          <div className="rv-kpi">
            <div className="rv-kpi-v">{krw(adv.required_monthly_investment)}</div>
            <div className="rv-kpi-l">
              적립 순수익률 {pct(adv.accumulation_net_return)} (연)
              · 연금소득세 {pct(adv.withdrawal_tax_rate)} 반영
            </div>
          </div>
          <p className="rv-note">
            총 납입액(추정): <b>{krw(adv.total_contributed)}</b>
          </p>
        </div>

        <div className="rv-card">
          <h2>일반계좌 월 납입액</h2>
          <div className="rv-kpi">
            <div className="rv-kpi-v">{krw(tax.required_monthly_investment)}</div>
            <div className="rv-kpi-l">
              적립 순수익률 {pct(tax.accumulation_net_return)} (연)
              · 이자/배당세 {pct(tax.withdrawal_tax_rate)} 반영
            </div>
          </div>
          <p className="rv-note">
            총 납입액(추정): <b>{krw(tax.total_contributed)}</b>
          </p>
        </div>
      </div>

      <div className="rv-card">
        <h2>목표 달성을 위한 월 납입액(추정)</h2>
        <BarChart
          labels={[adv.scenario_name, tax.scenario_name]}
          values={[adv.required_monthly_investment, tax.required_monthly_investment]}
          title="목표 달성을 위한 월 납입액(추정)"
          yTitle="만원"
          unit="manwon"
        />
        <p className="rv-note rv-warn">
          ⚠ 세금·수수료는 단순화 모델입니다.
          상품/세법/거래비용에 따라 실제 결과는 달라질 수 있습니다.
        </p>
      </div>

      <div className="rv-section">
        <div className="rv-card">
          <h2>요약 표</h2>
          <table className="rv-table">
            <thead>
              <tr><th>구분</th><th>절세계좌</th><th>일반계좌</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>적립구간 순수익률(연)</td>
                <td>{pct(adv.accumulation_net_return)}</td>
                <td>{pct(tax.accumulation_net_return)}</td>
              </tr>
              <tr>
                <td>은퇴구간 세율(단순)</td>
                <td>{pct(adv.withdrawal_tax_rate)}</td>
                <td>{pct(tax.withdrawal_tax_rate)}</td>
              </tr>
              <tr>
                <td>필요 은퇴자산</td>
                <td>{krw(adv.required_retirement_asset)}</td>
                <td>{krw(tax.required_retirement_asset)}</td>
              </tr>
              <tr>
                <td>필요 월 납입액</td>
                <td><b>{krw(adv.required_monthly_investment)}</b></td>
                <td><b>{krw(tax.required_monthly_investment)}</b></td>
              </tr>
              <tr>
                <td>총 납입액(추정)</td>
                <td>{krw(adv.total_contributed)}</td>
                <td>{krw(tax.total_contributed)}</td>
              </tr>
            </tbody>
          </table>
          <p className="rv-note">
            거래수수료(기본 {pct(CFG.TRADE_FEE_RATE)})는 월 납입 시
            매수 비용으로 단순 반영했습니다.
          </p>
        </div>
      </div>
      <div className="rv-page-break" />

      {/* ── Page 4: 자산 성장 전망 ───────────────────── */}
      <div className="rv-section-title">4) 적립 구간: 자산 성장 전망</div>

      <div className="rv-grid rv-grid-2">
        <div className="rv-card">
          <h2>{adv.scenario_name}</h2>
          <LineProjectionChart
            years={adv.projection_years}
            base={adv.projection_assets_base}
            cons={adv.projection_assets_conservative}
            opt={adv.projection_assets_optimistic}
            title={`자산 성장 전망 — ${adv.scenario_name}`}
          />
          <p className="rv-note">
            기준 수익률은 "목표 수익률 - 운용비용(연)"입니다
            (절세효과로 과세이연 단순 반영).
          </p>
        </div>

        <div className="rv-card">
          <h2>{tax.scenario_name}</h2>
          <LineProjectionChart
            years={tax.projection_years}
            base={tax.projection_assets_base}
            cons={tax.projection_assets_conservative}
            opt={tax.projection_assets_optimistic}
            title={`자산 성장 전망 — ${tax.scenario_name}`}
          />
          <p className="rv-note">
            일반계좌는 이자/배당 원천징수 세율(15.4%)을
            연간 세금 드래그로 단순 적용했습니다.
          </p>
        </div>
      </div>
      <div className="rv-page-break" />

      {/* ── Page 5: 은퇴 후 월 현금흐름 ─────────────── */}
      <div className="rv-section-title">5) 은퇴 후: 월 현금흐름(구성)</div>

      <div className="rv-card">
        <h2>월 수익 구성(예상)</h2>
        <CashflowStackChart
          npsMonthly={customer.expected_nps_monthly}
          portfolioMonthly={adv.needed_monthly_cashflow}
          targetMonthly={customer.target_monthly_pension}
        />
        <p className="rv-note">
          포트폴리오 인출은 "원금 유지(운용수익으로만 인출)" 가정입니다.<br />
          은퇴 운용수익률: {pct(customer.withdrawal_target_yield)} (연),
          운용비용 {pct(CFG.ANNUAL_MANAGEMENT_FEE)} 반영.
        </p>
      </div>
      <div className="rv-page-break" />

      {/* ── Page 6: 가정 및 유의사항 ─────────────────── */}
      <div className="rv-section-title">6) 가정 및 유의사항</div>

      <div className="rv-card">
        <h2>계산 가정(요약)</h2>
        <ul className="rv-note">
          <li>필요 현금흐름(갭) = max(0, 희망 월 수익 - 국민연금).</li>
          <li>
            필요 은퇴자산은 "이자/운용수익으로만 인출하여 원금 유지" 가정으로 산출.
          </li>
          <li>
            적립구간 수익률: 입력 목표 수익률에서 연 운용비용
            (기본 {pct(CFG.ANNUAL_MANAGEMENT_FEE)})을 차감.
          </li>
          <li>일반계좌 세금: 이자/배당 15.4%를 연간 세금 드래그로 단순 적용.</li>
          <li>절세계좌 세금: 연금소득세(연령별 3.3~5.5%)를 단순 적용.</li>
          <li>
            거래수수료: 월 납입 시 매수 수수료
            (기본 {pct(CFG.TRADE_FEE_RATE)})를 단순 반영.
          </li>
        </ul>
        <p className="rv-note rv-warn" style={{ marginTop: 12 }}>
          ⚠ 본 리포트는 입력값과 단순 가정에 기반한 "설계/교육용 추정치"입니다.
          실제 세금·수수료·상품 구조·시장 수익률·세법/제도 변경에 따라 결과는 달라질 수 있으며,
          투자 손실 가능성이 있습니다.
        </p>
      </div>

      <p className="rv-note" style={{ textAlign: 'center', marginBottom: 8 }}>
        ※ 모든 금액은 원 단위로 반올림한 추정치입니다.
      </p>

      <div className="rv-footer">
        © {new Date().getFullYear()} 라떼비버 은퇴 연금 리포트
        {reportId && <> · 리포트 ID: {reportId}</>}
      </div>
    </div>
  );
}
